import { useState, useEffect, useCallback, useRef } from "react";
import {
  getNewSeverityDistribution,
  getMonthlyRiskTrend,
  getActiveDashboardPrograms,
  getVulnerabilityTrend
} from "../../api/endpoints/dashboard/dashboardApi";

export interface SeverityDistribution {
  critical: number;
  high: number;
  medium: number;
  low: number;
  informational: number;
  total: number;
  periodInfo?: {
    label: string;
    startDate: string | null;
    endDate: string;
  };
}

export interface RiskTrendPoint {
  month: string;
  critical: number;
  high: number;
  medium: number;
  low: number;
  info?: number;
}

export interface ActiveProgram {
  program_id: number;
  name: string;
  status: string;
  due_date: string | null;
  target_types: string[];
}

export interface VulnerabilityTrendPoint {
  month: string;
  reported: number;
  resolved: number;
}

export interface ExecutiveSummaryData {
  severityDistribution?: SeverityDistribution;
  riskTrend?: RiskTrendPoint[];
  activePrograms?: ActiveProgram[];
  vulnerabilityTrend?: VulnerabilityTrendPoint[];
}

// Module-level in-memory cache for instant tab-switching (0ms perceived latency)
interface ExecutiveCacheEntry {
  data: ExecutiveSummaryData;
  timestamp: number;
}
const executiveSummaryCache = new Map<string, ExecutiveCacheEntry>();
const CACHE_FRESH_MS = 30_000;

function getExecutiveCacheKey(
  subscriptionOnly?: boolean,
  selectedYear?: number | null
): string {
  return `${subscriptionOnly ?? false}|${selectedYear ?? ""}`;
}

/**
 * Hook to fetch all data for the Executive Summary dashboard tab
 * Parallel fetching of four main metrics.
 * @returns Summary data, loading/error states, and refetch function
 */
export function useExecutiveSummaryData(subscriptionOnly?: boolean, selectedYear?: number | null, enabled: boolean = true) {
  const cacheKey = getExecutiveCacheKey(subscriptionOnly, selectedYear);
  const cached = executiveSummaryCache.get(cacheKey);

  const [data, setData] = useState<ExecutiveSummaryData | null>(cached ? cached.data : null);
  const [loading, setLoading] = useState<boolean>(!cached);
  const [error, setError] = useState<string | null>(null);
  const latestFetchRef = useRef<(force?: boolean) => void>(() => {});

  useEffect(() => {
    if (!enabled) return;
    let active = true;

    const currentCached = executiveSummaryCache.get(cacheKey);
    if (currentCached) {
      setData(currentCached.data);
      setLoading(false);
    } else {
      setLoading(true);
    }

    const fetchData = async (force: boolean = false) => {
      const entry = executiveSummaryCache.get(cacheKey);
      if (!force && entry && Date.now() - entry.timestamp < CACHE_FRESH_MS) {
        if (active) {
          setData(entry.data);
          setLoading(false);
        }
        return;
      }

      try {
        if (!entry) {
          setLoading(true);
        }
        setError(null);

        // Fetch all metrics in parallel with progressive streaming updates
        const defaultTimeRange = (!subscriptionOnly && selectedYear === null) ? "all" : "yearly";

        const p1 = getNewSeverityDistribution(defaultTimeRange, subscriptionOnly, selectedYear)
          .then((res) => {
            if (active && res?.success && res.data) {
              setData((prev) => ({ ...(prev || {}), severityDistribution: res.data } as ExecutiveSummaryData));
            }
            return res;
          })
          .catch((err) => {
            console.error("Failed to load severity distribution:", err);
            return null;
          });

        const p2 = getMonthlyRiskTrend(defaultTimeRange, subscriptionOnly, selectedYear)
          .then((res) => {
            if (active && res?.success) {
              const trend = res.data?.data || res.data || [];
              setData((prev) => ({ ...(prev || {}), riskTrend: trend } as ExecutiveSummaryData));
            }
            return res;
          })
          .catch((err) => {
            console.error("Failed to load risk trend:", err);
            return null;
          });

        const p3 = getActiveDashboardPrograms(subscriptionOnly, selectedYear)
          .then((res) => {
            if (active && res?.success && res.data) {
              const programs = (res.data || []).map((p: any) => ({
                ...p,
                name: p.program_title || "Untitled Program",
                target_types: typeof p.target_types === 'string'
                  ? p.target_types.split(',').map((s: string) => s.trim()).filter((s: string) => s && s !== '—')
                  : Array.isArray(p.target_types) ? p.target_types : []
              }));
              setData((prev) => ({ ...(prev || {}), activePrograms: programs } as ExecutiveSummaryData));
            }
            return res;
          })
          .catch((err) => {
            console.error("Failed to load active programs:", err);
            return null;
          });

        const p4 = getVulnerabilityTrend(defaultTimeRange, subscriptionOnly, selectedYear)
          .then((res) => {
            if (active && res?.success) {
              const vulnTrend = res.data?.data || res.data || [];
              setData((prev) => ({ ...(prev || {}), vulnerabilityTrend: vulnTrend } as ExecutiveSummaryData));
            }
            return res;
          })
          .catch((err) => {
            console.error("Failed to load vulnerability trend:", err);
            return null;
          });

        const [
          severityResponse,
          riskTrendResponse,
          activeProgramsResponse,
          vulnTrendResponse
        ] = await Promise.all([p1, p2, p3, p4]);

        if (!active) return;

        if (
          severityResponse?.success &&
          riskTrendResponse?.success &&
          activeProgramsResponse?.success &&
          vulnTrendResponse?.success
        ) {
          const freshData: ExecutiveSummaryData = {
            severityDistribution: severityResponse.data,
            riskTrend: riskTrendResponse.data?.data || riskTrendResponse.data || [],
            activePrograms: (activeProgramsResponse.data || []).map((p: any) => ({
              ...p,
              name: p.program_title || "Untitled Program",
              target_types: typeof p.target_types === 'string'
                ? p.target_types.split(',').map((s: string) => s.trim()).filter((s: string) => s && s !== '—')
                : Array.isArray(p.target_types) ? p.target_types : []
            })),
            vulnerabilityTrend: vulnTrendResponse.data?.data || vulnTrendResponse.data || []
          };

          executiveSummaryCache.set(cacheKey, {
            data: freshData,
            timestamp: Date.now()
          });

          setData(freshData);
        } else {
          // Find which one failed for better error logging
          const failures = [];
          if (!severityResponse?.success) failures.push("Severity Distribution");
          if (!riskTrendResponse?.success) failures.push("Risk Trend");
          if (!activeProgramsResponse?.success) failures.push("Active Programs");
          if (!vulnTrendResponse?.success) failures.push("Vulnerability Trend");

          setError(`Failed to load: ${failures.join(", ")}`);
        }
      } catch (err) {
        if (!active) return;
        console.error("Error fetching executive summary data:", err);
        setError("Failed to load dashboard metrics");
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    latestFetchRef.current = (force = true) => fetchData(force);
    fetchData();

    return () => {
      active = false;
    };
  }, [cacheKey, subscriptionOnly, selectedYear, enabled]);

  const refetch = useCallback(() => {
    latestFetchRef.current(true);
  }, []);

  return {
    data,
    loading,
    error,
    refetch
  };
}
